import React, { useEffect, useState } from 'react';
import { getLabDataQuality } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabMetric from '../components/LabMetric';
import LabRiskBadge from '../components/LabRiskBadge';
import LabTable from '../components/LabTable';
import { useSmartiLabContext } from '../SmartiLabContext';

function issueRows(issues = []) {
  return issues.map((issue, index) => ({
    id: `${issue.title}-${index}`,
    ...issue,
  }));
}

export default function DataQualityPage() {
  const { sharedQuery } = useSmartiLabContext();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getLabDataQuality(sharedQuery).then(setData).catch((err) => setError(err.message));
  }, [sharedQuery]);

  return (
    <div className="lab-page">
      <LabCard title="Qualità e copertura dati" description="Verifica se l’evidenza del database è abbastanza solida per fidarsi delle conclusioni di Smarti Lab.">
        {error ? <p className="lab-error-text">{error}</p> : null}
        <div className="lab-metric-grid">
          <LabMetric label="% stazioni prod" value={`${data?.production?.stationCoverage ?? 0}%`} />
          <LabMetric label="% limiti prod" value={`${data?.production?.limitCoverage ?? 0}%`} />
          <LabMetric label="% numerico burn-in" value={`${data?.burnIn?.numericCoverage ?? 0}%`} />
          <LabMetric label="Seriali collegati" value={data?.linkage?.matchedProductionSerials ?? 0} />
        </div>
      </LabCard>

      <div className="lab-grid two-col">
        <LabCard title="Copertura produzione" description="Completezza dei campi che influenza analisi di unità, stazioni, regressioni e deriva.">
          <div className="lab-metric-grid">
            <LabMetric label="Test" value={data?.production?.totalTests ?? 0} />
            <LabMetric label="Step" value={data?.production?.totalSteps ?? 0} />
            <LabMetric label="ATE %" value={`${data?.production?.ateCoverage ?? 0}%`} />
            <LabMetric label="Testspec %" value={`${data?.production?.testspecCoverage ?? 0}%`} />
          </div>
          <LabTable
            rows={issueRows(data?.production?.issues)}
            emptyTitle="La copertura produzione è utilizzabile"
            emptyDescription="Non sono stati rilevati problemi importanti di copertura produzione nello scope corrente."
            columns={[
              { key: 'severity', label: 'Severità', render: (value) => <LabRiskBadge label={value} /> },
              { key: 'title', label: 'Problema' },
              { key: 'summary', label: 'Sintesi' },
            ]}
          />
        </LabCard>

        <LabCard title="Copertura burn-in" description="Completezza dei campi che influenza degrado, analisi sorgenti e collegamento cross-dataset.">
          <div className="lab-metric-grid">
            <LabMetric label="Upload" value={data?.burnIn?.totalUploads ?? 0} />
            <LabMetric label="Misure" value={data?.burnIn?.totalMeasurements ?? 0} />
            <LabMetric label="Measured_at %" value={`${data?.burnIn?.measuredAtCoverage ?? 0}%`} />
            <LabMetric label="Source label %" value={`${data?.burnIn?.sourceLabelCoverage ?? 0}%`} />
          </div>
          <LabTable
            rows={issueRows(data?.burnIn?.issues)}
            emptyTitle="La copertura burn-in è utilizzabile"
            emptyDescription="Non sono stati rilevati problemi importanti di copertura burn-in nello scope corrente."
            columns={[
              { key: 'severity', label: 'Severità', render: (value) => <LabRiskBadge label={value} /> },
              { key: 'title', label: 'Problema' },
              { key: 'summary', label: 'Sintesi' },
            ]}
          />
        </LabCard>
      </div>

      <LabCard title="Qualità del collegamento" description="Quanto bene, in questo momento, i seriali di produzione e le source label burn-in possono essere collegati.">
        <div className="lab-metric-grid">
          <LabMetric label="Seriali collegati" value={data?.linkage?.matchedProductionSerials ?? 0} />
          <LabMetric label="Seriali non collegati" value={data?.linkage?.unmatchedProductionSerials ?? 0} />
          <LabMetric label="Sorgenti collegate" value={data?.linkage?.matchedBurnSources ?? 0} />
          <LabMetric label="Sorgenti non collegate" value={data?.linkage?.unmatchedBurnSources ?? 0} />
        </div>
        <LabTable
          rows={issueRows(data?.linkage?.issues)}
          emptyTitle="La qualità del collegamento è accettabile"
          emptyDescription="Non sono stati rilevati problemi importanti di collegamento cross-dataset nello scope corrente."
          columns={[
            { key: 'severity', label: 'Severità', render: (value) => <LabRiskBadge label={value} /> },
            { key: 'title', label: 'Problema' },
            { key: 'summary', label: 'Sintesi' },
          ]}
        />
      </LabCard>
    </div>
  );
}
